import { enableProdMode, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { bootstrapApplication } from "@angular/platform-browser";
import { registerLocaleData } from '@angular/common';
import localeEsAR from '@angular/common/locales/es-AR';

// Registrar locale es-AR globalmente para todos los pipes (DecimalPipe, DatePipe, etc.).
// Sin esto, `{{ value | number:'1.0-0':'es-AR' }}` tira NG02100 / NG0701 que rompe el
// rendering de DevExtreme grids (las filas posteriores al error quedan en blanco).
registerLocaleData(localeEsAR, 'es-AR');
import {
  AdminGuardService,
  AppInfoService,
  AuthGuardService,
  AuthService,
  ScreenService,
  ThemeService
} from "./app/services";
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { provideRouter, withHashLocation, withViewTransitions } from "@angular/router";

import themes from 'devextreme/ui/themes';
import config from 'devextreme/core/config';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { routes } from "./app/app-routes";
import licenseKey from './devextreme-license';
import { AuthInterceptor } from './app/services/auth.interceptor';
import { AcceptLanguageInterceptor } from './app/services/accept-language.interceptor';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LanguageService } from './app/services/language.service';

/**
 * Loader factory para ngx-translate — lee `assets/i18n/{lang}.json` relativo al
 * base-href de la app. Sin `./` inicial: en deploys con `--base-href=/CRM/` el
 * `./assets/...` se resolvía contra la URL del documento actual y rompía cuando
 * se navegaba con hash routing (`#/auth/sign-in`). El path "desnudo" deja que
 * Angular use el `<base href>` correcto.
 */
export function httpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

config({ licenseKey });

if (environment.production) {
  enableProdMode();
}

themes.initialized(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      AuthService,
      ScreenService,
      AppInfoService,
      ThemeService,
      AuthGuardService,
      AdminGuardService,
      { provide: LOCALE_ID, useValue: 'es-AR' },
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      // Accept-Language interceptor — adjunta el idioma activo a cada request del API.
      { provide: HTTP_INTERCEPTORS, useClass: AcceptLanguageInterceptor, multi: true },
      importProvidersFrom(HttpClientModule),
      provideHttpClient(withInterceptorsFromDi()),
      // ngx-translate: carga JSONs desde assets/i18n/{lang}.json. El LanguageService
      // se instancia eagerly (providedIn: root + injection en AppComponent) y dispara
      // `translate.use(currentLang)` + DevExtreme `locale()` al arrancar.
      importProvidersFrom(
        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader,
            useFactory: httpLoaderFactory,
            deps: [HttpClient],
          },
          defaultLanguage: 'es',
        }),
      ),
      LanguageService,
      // View Transitions API — anima los cambios de ruta usando el browser nativo
      // (Chrome 111+, Edge, Opera). En Safari/Firefox sin soporte degrada a navegar
      // sin animacion. El CSS de `::view-transition-*` esta en styles.scss.
      //
      // `skipInitialTransition: true` evita el flash al primer render (cuando no
      // hay ruta previa con la cual cross-fade). `onViewTransitionCreated` permite
      // skipear la transicion en navegaciones donde no la queremos (ej. mismo path
      // con queryParams distintos — un drill-through del dashboard no deberia
      // disparar fade del shell completo).
      provideRouter(
        routes,
        withHashLocation(),
        withViewTransitions({
          skipInitialTransition: true,
          onViewTransitionCreated: ({ transition, from, to }) => {
            // Si solo cambian queryParams o fragment (misma URL base), no animamos —
            // el contenido del componente no cambio.
            if (from && to && from.url.toString().split('?')[0] === to.url.toString().split('?')[0]) {
              transition.skipTransition();
            }
            // El `transition.finished` rechaza con AbortError cuando skipeamos.
            // Es benigno pero loggea en consola si nadie lo handlea. Lo silenciamos
            // explicitamente — el caller no necesita saber que skipeamos.
            transition.finished?.catch(() => { /* AbortError esperado al skipear */ });
          },
        }),
      ),
    ]
  })
    .then(() => hidePreloader())
    .catch((e) => {
      console.error(e);
      hidePreloader();
    });
});

function hidePreloader() {
  const loader = document.getElementById('app-loader');
  if (!loader) return;
  // Pequeño delay para que el loader se vea al menos 400ms (evita flash de pantalla).
  setTimeout(() => {
    loader.classList.add('is-hiding');
    setTimeout(() => loader.remove(), 500);
  }, 400);
}

